const CONSENT_KEY = 'pdf911:analytics-consent';
const CHANGE_EVENT = 'pdf911:analytics-consent-change';
const ANALYTICS_ENABLED = true;
const GA_MEASUREMENT_ID = 'G-P492VZL0QS';

type AnalyticsConsent = 'accepted' | 'declined' | null;
type GoalParams = Record<string, string | number | boolean | null | undefined>;
type GtagCommand = 'config' | 'event' | 'js';
type Gtag = (command: GtagCommand, target: string | Date, params?: Record<string, unknown>) => void;
type AnalyticsWindow = Window & {
  dataLayer?: unknown[][];
  gtag?: Gtag;
};

let initialized = false;
let lastHitUrl = '';

export function isMetrikaHost(): boolean {
  const host = location.hostname.toLowerCase();
  return ANALYTICS_ENABLED && (host === 'pdf911.com' || host === 'www.pdf911.com');
}

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}

export function setAnalyticsConsent(next: Exclude<AnalyticsConsent, null>): void {
  try {
    localStorage.setItem(CONSENT_KEY, next);
  } catch {
    // If localStorage is blocked, the banner can ask again on the next visit.
  }
  syncGaDisableFlag();
  if (next === 'accepted') sendCurrentPageView(document.referrer);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
}

export function initMetrika(): void {
  if (!isMetrikaHost()) return;
  initialized = true;
  syncGaDisableFlag();
  if (getAnalyticsConsent() !== 'declined' && !lastHitUrl) lastHitUrl = currentUrl();
}

export function trackPageView(): void {
  if (!canTrack()) return;
  const url = currentUrl();
  if (url === lastHitUrl) return;
  const referer = lastHitUrl || document.referrer;
  sendPageView(url, referer);
}

export function hasAnalyticsProvider(): boolean {
  return isMetrikaHost();
}

function sendPageView(url: string, referer: string): void {
  lastHitUrl = url;
  callConfig({
    page_location: url,
    page_path: `${location.pathname}${location.search}${location.hash}`,
    page_title: document.title,
    page_referrer: referer,
    transport_type: 'beacon',
  });
}

export function trackGoal(name: string, params?: GoalParams): void {
  if (!canTrack()) return;
  callAnalytics('event', normalizeParam(name), sanitizeParams(params));
}

export function trackFileSelected(files: File[]): void {
  if (!files.length) return;
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const kinds = [...new Set(files.map(fileKind))];
  trackGoal('file_selected', {
    tool: currentTool(),
    files_count: Math.min(files.length, 50),
    file_kind: kinds.length === 1 ? kinds[0] : 'mixed',
    size_bucket: sizeBucket(totalSize),
  });
}

export function trackResultReady(mime: string, bytes?: number): void {
  trackGoal('result_ready', {
    tool: currentTool(),
    result_kind: mimeKind(mime),
    size_bucket: bytes ? sizeBucket(bytes) : undefined,
  });
}

export function trackDownload(mime?: string): void {
  trackGoal('download_click', {
    tool: currentTool(),
    result_kind: mime ? mimeKind(mime) : undefined,
  });
}

export function trackToolError(error: unknown, scope = 'tool'): void {
  trackGoal('tool_error', {
    tool: currentTool(),
    scope,
    error_kind: errorKind(error),
  });
}

function canTrack(): boolean {
  if (!isMetrikaHost()) return false;
  initMetrika();
  return initialized && getAnalyticsConsent() !== 'declined';
}

function syncGaDisableFlag(): void {
  (window as unknown as Record<string, boolean | undefined>)[`ga-disable-${GA_MEASUREMENT_ID}`] = getAnalyticsConsent() === 'declined';
}

function sendCurrentPageView(referer: string): void {
  const url = currentUrl();
  if (url !== lastHitUrl) sendPageView(url, referer);
}

function callAnalytics(command: 'event', eventName: string, params: Record<string, unknown>): void {
  const gtag = (window as AnalyticsWindow).gtag;
  if (!gtag) return;
  gtag(command, eventName, params);
}

function callConfig(params: Record<string, unknown>): void {
  const gtag = (window as AnalyticsWindow).gtag;
  if (!gtag) return;
  gtag('config', GA_MEASUREMENT_ID, params);
}

function currentUrl(): string {
  return `${location.origin}${location.pathname}${location.search}${location.hash}`;
}

function currentTool(): string {
  const hashTool = location.hash.replace(/^#\/?/, '').split('/')[0];
  if (hashTool) return normalizeParam(hashTool);
  const pathTool = location.pathname.replace(/^\/+|\/+$/g, '');
  return normalizeParam(pathTool || 'home');
}

function sanitizeParams(params?: GoalParams): Record<string, string | number | boolean> {
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;
    safe[normalizeParam(key)] = typeof value === 'string' ? normalizeParam(value) : value;
  }
  return safe;
}

function normalizeParam(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'unknown';
}

function fileKind(file: File): string {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (type.includes('wordprocessingml') || name.endsWith('.docx')) return 'docx';
  if (type.includes('spreadsheetml') || name.endsWith('.xlsx')) return 'xlsx';
  if (type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(name)) return 'image';
  if (type.includes('csv') || name.endsWith('.csv')) return 'csv';
  if (type.includes('text') || name.endsWith('.txt')) return 'text';
  return 'other';
}

function mimeKind(mime: string): string {
  const value = mime.toLowerCase();
  if (value.includes('pdf')) return 'pdf';
  if (value.includes('zip')) return 'zip';
  if (value.includes('jpeg') || value.includes('jpg')) return 'jpg';
  if (value.includes('png')) return 'png';
  if (value.includes('word') || value.includes('docx')) return 'docx';
  if (value.includes('plain') || value.includes('text')) return 'text';
  return 'other';
}

function sizeBucket(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb < 1) return '0_1mb';
  if (mb < 10) return '1_10mb';
  if (mb < 50) return '10_50mb';
  if (mb < 200) return '50_200mb';
  return '200mb_plus';
}

function errorKind(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/parse|invalid pdf|pdf document/i.test(message)) return 'pdf_parse';
  if (/password|encrypt|protected/i.test(message)) return 'encrypted_pdf';
  if (/memory|allocation|quota/i.test(message)) return 'memory';
  if (/network|fetch|load/i.test(message)) return 'load';
  return 'other';
}
